import { all, Div, Span } from 'alkali'
import { selectedOutcomes, groupedInterventions } from 'model/EvidenceSearch'
import { analysisType, analyzeTreatmentAs, analysisSelections, structuredQuery } from 'model/SearchQuery'
import { metaAnalysisResults } from 'model/MetaAnalysis'
import { analysisResults, view as analysisView } from 'model/Analysis'
import { studyCountForComparisonEffect } from 'model/GroupConnectivity'
import { digitizedStudiesLoaded } from 'model/StudyCollection'
import { followUpDescription } from '../filter/Demographics'

export class EvidenceStatement extends Div('.evidence-statement') {
	created(properties) {
		properties.content = all(
			[
				analysisType,
				selectedOutcomes,
				groupedInterventions,
				properties.activeTab,
				analysisResults,
				followUpDescription,
			],
			async (analysisType, outcomes, treatments, activeTab, [ analysisResults ], followUpDescription) => {
				if (activeTab && activeTab != 'report') return Span([''])
				let outcome = outcomes[0]
				let effect
				let totalN
				let marginOfError
				let increases
				let isContinuous
				if (!analysisResults || !outcome)
					return Span['No analysis results returned']
				let interventions
				let treatmentEffects = analysisResults.interventions
				if (!treatmentEffects?.[0])
					return Span['No analysis results returned']
				await digitizedStudiesLoaded
				let treatment = treatmentEffects.find(treatment => !treatment.isBaseline && (!properties.arm || treatment.name == properties.arm)) || treatmentEffects[0]
				let treatmentName = treatment.name
				let comparativeTreatment = (treatmentEffects.length > 1 ?
					treatmentEffects.find(treatment => treatment.isBaseline) || treatmentEffects[treatmentEffects.length - 1] : {}).name
				if (treatmentEffects.length == 2 && comparativeTreatment == 'Control')
					comparativeTreatment = null
				let isCohort = treatmentEffects.length == 1
				let effectSize = treatment.effect
				let lowerBound = treatment.lowerBound
				let upperBound = treatment.upperBound
				let settings = analysisSelections.settings.valueOf() || {}
				if (settings.reverseDirection) {
					effectSize = -effectSize
					lowerBound = -lowerBound
					upperBound = -upperBound
				}
				isContinuous = analysisResults.dataType == 'cont'
				if (!isContinuous) {
					let measureBin = settings.measureBin || 'RR'
					let isRatio = measureBin != 'RD'
					let backTransform =
						isRatio ?
							isCohort ?
								measureBin == 'OR' ? value => {
										let odds = Math.exp(value)
										return odds / (1 + odds)
									} :
									Math.exp :
								measureBin == 'OR' ? value => {
										let oddsRatio = Math.exp(value)
										let risk = treatment.value / 100
										let odds = risk / (1 - risk)
										let comparatorOdds = odds / oddsRatio
										return risk / (comparatorOdds / (1 + comparatorOdds)) - 1
									} :
									(value) => Math.exp(value) - 1 :
							(value) => value
					let ratioDifference = backTransform(effectSize)
					effectSize = ratioDifference * 100
					lowerBound = backTransform(lowerBound) * 100
					upperBound = backTransform(upperBound) * 100
				}
				marginOfError = upperBound - lowerBound
				effect = {
					effect: effectSize,
					lowerBound,
					upperBound,
					studies: treatment.studyCount,
				}
				totalN = treatmentEffects.reduce((sum, treatment) => treatment.N + sum, 0)
				if (!effect) return ''
				increases = effect.effect >= 0
				let isChange = outcome.type == 'Change' || outcome.type == '% Change'
				if (!increases && !(isCohort && !isChange)) {
					effect.effect = -effect.effect
					let lowerBound = -effect.upperBound
					effect.upperBound = -effect.lowerBound
					effect.lowerBound = lowerBound
				}
				let unit = isContinuous ? outcome.unit ? ' ' + outcome.unit : '' : '%'
				let effectPhrase = [Span('.statement-emphasis', [numToString(effect.effect) + unit]),
						((effect.lowerBound || effect.upperBound) ? ' (' + numToString(effect.lowerBound) + unit + ' to ' + numToString(effect.upperBound) + unit + ')' : '')]

				let riskWord = ''
				let riskType = ''
				if (outcome.type == 'Binary') {
					riskWord = (isCohort ? ' a' : ' the') + (outcome.isPositive ? ' likelihood of ' : ' risk of ')
				}
				let outcomeLabel = structuredQuery.outcomes.valueOf()[0]?.label || outcome.label
				let outcomeNode = Span('.statement-emphasis', [ outcomeLabel ])
				let mainTreatmentNode = Span('.statement-emphasis', [ treatmentName ])
				let isNMA = treatmentEffects.length > 2
				let bestTreatmentPhrase = isNMA ? [', the best of ' +
					treatmentEffects.filter(treatment => treatment.name != 'Control').length + ' treatments from ' + analysisResults.studyCount + ' studies'] : []
				let comparativePhrase = isContinuous ?
						isChange ?
							increases ? ' higher by ' : ' lower by '
							: increases ? ' increased by ' : ' reduced by '
						: increases ? outcome.isPositive ? '' : ' more likely' : ' less likely'
				let evidencePhrase = comparativeTreatment ?
					[Span('.statement-evidently.linear-text', ['Evidently, ']), riskType, outcomeNode,
					((!outcome.isPositive && increases && treatmentEffects.length > 2) ? ' was only ' :
						(outcome.isPositive && increases && !comparativePhrase) ? ' was improved by ' : ' was '),
					...(isContinuous ? [comparativePhrase, ...effectPhrase] : [...effectPhrase, comparativePhrase]),
					' with ', mainTreatmentNode, ', compared with ',
					Span('.statement-emphasis', [comparativeTreatment])] :
					isCohort ?
						[...(treatmentName == 'Control' ? [Span('.statement-evidently.linear-text', ['Evidently, ']), 'patients '] : [Span('.statement-evidently.linear-text', ['Evidently, ']), 'patients using ', mainTreatmentNode]), ' have ',
							isContinuous ?
								isChange ? increases ? ' an increased ' : ' a reduced ' : ' an average ' : riskWord,
							outcomeNode, ' of ', ...effectPhrase] :
						[Span('.statement-evidently.linear-text', ['Evidently, ']), mainTreatmentNode,
							increases ? ' increases ' : ' reduces ',
							isContinuous ? ' ' : riskWord, outcomeNode, ' by ',
							...effectPhrase]
				let studyCount = Math.min(comparativeTreatment ? await studyCountForComparisonEffect(treatmentName, comparativeTreatment) : analysisResults.studyCount, analysisResults.studyCount)
				return Span([
					...evidencePhrase,
					', based on ',
					Span('.statement-emphasis', [studyCount == 1 ? ' one study' : studyCount + ' studies']),
					...(totalN ? [' including ', Span('.statement-emphasis', totalN.toLocaleString() + ' participants')] : []),
					...(followUpDescription ? [' over an average of ', Span('.statement-emphasis', [followUpDescription])] : []),
					...bestTreatmentPhrase, '.'
				])
			}
		).whileResolving(new Span('.linear-text.loading', ['Analyzing studies..']))
	}
}
const numToString = x =>
	x
		.toFixed(Math.min(Math.max(-Math.floor(Math.log10(Math.abs(x))) + 2, 0), 8))
		.replace(/\.(\d*?)0+$/, (t, mantissa) => (mantissa ? '.' + mantissa : ''))